// Certificates in the browser (rules: src/core/certificates.js).
//
// Real accounts: certificates are listed from public.certificates (own rows only), issued by the
// issue-certificate Edge Function (which checks server-verified results) and checked publicly with
// the verify_certificate() database function. Development sign-in (no Supabase): kept in this
// browser under nell:v1:dev:certificates so the screens can be used and tested offline — those are
// NOT real certificates and the page says so.
import { makeCertId, normalizeCertId, cleanHolderName, CERT_KINDS } from './certificates.js';

const DEV_KEY = 'certificates';
const pub = (c) => (c ? { id: c.id, kind: c.kind, holder_name: c.holder_name, issued_at: c.issued_at } : null);

/**
 * @param {{ client?: object|null, devStore: object, user: () => (object|null) }} o
 */
export function createCertificates({ client = null, devStore, user }) {
  const real = !!client;
  const devAll = () => devStore.get(DEV_KEY, {}) || {};
  return {
    real,
    /** This learner's certificates. */
    async list() {
      const u = user();
      if (!u) return [];
      if (!real) return Object.values(devAll()).filter((c) => c.user_id === (u.id || u.email || u.name)).map(pub);
      const { data, error } = await client.from('certificates').select('id, kind, holder_name, issued_at').eq('user_id', u.id);
      if (error) throw error;
      return (data || []).map(pub);
    },
    /**
     * Ask for a certificate. Development sign-in only issues when `eligible` (worked out locally).
     * @returns {Promise<{ ok: boolean, certificate?: object, error?: string, done?: number, total?: number }>}
     */
    async issue(kind, name, { eligible = false } = {}) {
      const u = user();
      if (!u) return { ok: false, error: 'Sign in first.' };
      if (!CERT_KINDS.includes(kind)) return { ok: false, error: 'Unknown certificate.' };
      if (!real) {
        const holder = cleanHolderName(name);
        if (!holder) return { ok: false, error: 'Enter the name to print: letters, spaces, apostrophes or hyphens (2–60 characters).' };
        if (!eligible) return { ok: false, error: 'Not complete yet.' };
        const all = devAll();
        const owner = u.id || u.email || u.name;
        const have = Object.values(all).find((c) => c.user_id === owner && c.kind === kind);
        if (have) return { ok: true, certificate: pub(have) };
        const c = { id: makeCertId((n) => Math.floor(Math.random() * n)), user_id: owner, kind, holder_name: holder, issued_at: new Date().toISOString(), dev: true };
        devStore.set(DEV_KEY, { ...all, [c.id]: c });
        return { ok: true, certificate: pub(c) };
      }
      const { data, error } = await client.functions.invoke('issue-certificate', { body: { kind, name } });
      if (!error && data?.certificate) return { ok: true, certificate: pub(data.certificate) };
      let info = data || null;
      try { if (!info && error?.context?.json) info = await error.context.json(); } catch { info = null; }
      return { ok: false, error: info?.error || 'Could not issue the certificate right now. Try again in a moment.', done: info?.done, total: info?.total };
    },
    /** Public check by ID (anyone). Resolves to the certificate or null. */
    async lookup(code) {
      const id = normalizeCertId(code);
      if (!id) return null;
      if (!real) { const c = devAll()[id]; return c ? { ...pub(c), dev: true } : null; }
      const { data, error } = await client.rpc('verify_certificate', { code: id });
      if (error) throw error;
      return pub(Array.isArray(data) ? data[0] : data) || null;
    },
  };
}
